<template>
  <div class="ts-table-wrap">
    <table class="ts-table">
      <thead>
        <tr>
          <th class="ts-table__rowhead">Monat</th>
          <th
            v-for="month in orderedMonths"
            :key="`head_${month.monat}`"
          >
            {{ monthLabel(month.monat) }}
          </th>
          <th class="ts-table__cumhead">Kumuliert</th>
        </tr>
      </thead>
      <tbody>
        <tr class="ts-table__row--primary ts-table__row--ist">
          <td class="ts-table__metric">IST</td>
          <td v-for="month in orderedMonths" :key="`ist_${month.monat}`" :class="emptyClass(istByMonth.get(month.monat))">{{ fmtCell(istByMonth.get(month.monat)) }}</td>
          <td class="ts-table__cum">{{ fmtCell(cum.ist) }}</td>
        </tr>
        <tr class="ts-table__row--primary ts-table__row--plan">
          <td class="ts-table__metric">PLAN</td>
          <td v-for="month in orderedMonths" :key="`plan_${month.monat}`" :class="emptyClass(planByMonth.get(month.monat))">{{ fmtCell(planByMonth.get(month.monat)) }}</td>
          <td class="ts-table__cum">{{ fmtCell(cum.plan) }}</td>
        </tr>
        <tr class="ts-table__row--delta">
          <td class="ts-table__metric ts-table__metric--delta">vs. Plan</td>
          <td
            v-for="month in orderedMonths"
            :key="`dplan_${month.monat}`"
            :class="deltaClassForMetric(deltaPlanByMonth.get(month.monat))"
          >
            {{ fmtDelta(deltaPlanByMonth.get(month.monat)) }}
          </td>
          <td class="ts-table__cum" :class="deltaClassForMetric(cum.deltaPlan)">{{ fmtDelta(cum.deltaPlan) }}</td>
        </tr>
        <tr class="ts-table__row--primary ts-table__row--vj">
          <td class="ts-table__metric">VJ</td>
          <td v-for="month in orderedMonths" :key="`vj_${month.monat}`" :class="emptyClass(vjByMonth.get(month.monat))">{{ fmtCell(vjByMonth.get(month.monat)) }}</td>
          <td class="ts-table__cum">{{ fmtCell(cum.vj) }}</td>
        </tr>
        <tr class="ts-table__row--delta">
          <td class="ts-table__metric ts-table__metric--delta">vs. VJ</td>
          <td
            v-for="month in orderedMonths"
            :key="`dvj_${month.monat}`"
            :class="deltaClassForMetric(deltaVjByMonth.get(month.monat))"
          >
            {{ fmtDelta(deltaVjByMonth.get(month.monat)) }}
          </td>
          <td class="ts-table__cum" :class="deltaClassForMetric(cum.deltaVj)">{{ fmtDelta(cum.deltaVj) }}</td>
        </tr>
      </tbody>
    </table>
  </div>
</template>

<script>
const MONTHS = ['Jan', 'Feb', 'Mär', 'Apr', 'Mai', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dez'];
const EUR = new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 });

function fmtDelta(v) {
  if (v === null || v === undefined) return '–';
  const s = EUR.format(Math.abs(v));
  return v >= 0 ? `+${s}` : `−${s}`;
}

function deltaClass(v) {
  if (v === null || v === undefined) return '';
  return v >= 0 ? 'positive' : 'negative';
}

function buildVorjahrByMonth(vorjahr) {
  const map = new Map();
  for (const row of vorjahr || []) {
    if (row?.monat !== null && row?.monat !== undefined) {
      map.set(Number(row.monat), row);
    }
  }
  return map;
}

export default {
  props: {
    months: { type: Array, required: true },
    vorjahr: { type: Array, default: () => [] },
    metric: { type: String, required: true },
    effectiveMonth: { type: Number, default: null },
  },
  computed: {
    isKostenMetric() {
      return this.metric.includes('kosten');
    },
    istKey() {
      return `${this.metric}_ist`;
    },
    planKey() {
      return `${this.metric}_plan`;
    },
    orderedMonths() {
      return [...this.months].sort((a, b) => Number(a.monat) - Number(b.monat));
    },
    vorjahrByMonth() {
      return buildVorjahrByMonth(this.vorjahr);
    },
    istByMonth() {
      const map = new Map();
      for (const m of this.orderedMonths) {
        const monthNum = Number(m.monat);
        const ist = m[this.istKey];
        const hasIst = ist !== null && ist !== undefined && monthNum <= (this.effectiveMonth ?? 12);
        map.set(monthNum, hasIst ? Number(ist) : null);
      }
      return map;
    },
    planByMonth() {
      const map = new Map();
      for (const m of this.orderedMonths) {
        const monthNum = Number(m.monat);
        const plan = m[this.planKey];
        map.set(monthNum, plan !== null && plan !== undefined ? Number(plan) : null);
      }
      return map;
    },
    vjByMonth() {
      const map = new Map();
      for (const m of this.orderedMonths) {
        const monthNum = Number(m.monat);
        const vj = this.vorjahrByMonth.get(monthNum)?.[this.istKey];
        map.set(monthNum, vj !== null && vj !== undefined ? Number(vj) : null);
      }
      return map;
    },
    deltaPlanByMonth() {
      const map = new Map();
      for (const m of this.orderedMonths) {
        const monthNum = Number(m.monat);
        const ist = this.istByMonth.get(monthNum);
        const plan = this.planByMonth.get(monthNum);
        map.set(monthNum, ist !== null && plan !== null ? ist - plan : null);
      }
      return map;
    },
    deltaVjByMonth() {
      const map = new Map();
      for (const m of this.orderedMonths) {
        const monthNum = Number(m.monat);
        const ist = this.istByMonth.get(monthNum);
        const vj = this.vjByMonth.get(monthNum);
        map.set(monthNum, ist !== null && vj !== null ? ist - vj : null);
      }
      return map;
    },
    cum() {
      let ist = 0;
      let plan = 0;
      let vj = 0;
      let hasIst = false;
      let hasPlan = false;
      let hasVj = false;

      for (const m of this.orderedMonths) {
        const monthNum = Number(m.monat);
        if (monthNum > (this.effectiveMonth ?? 12)) continue;

        const i = this.istByMonth.get(monthNum);
        const p = this.planByMonth.get(monthNum);
        const y = this.vjByMonth.get(monthNum);

        if (i !== null) {
          ist += i;
          hasIst = true;
        }
        if (p !== null) {
          plan += p;
          hasPlan = true;
        }
        if (y !== null) {
          vj += y;
          hasVj = true;
        }
      }

      return {
        ist: hasIst ? ist : null,
        plan: hasPlan ? plan : null,
        vj: hasVj ? vj : null,
        deltaPlan: hasIst && hasPlan ? ist - plan : null,
        deltaVj: hasIst && hasVj ? ist - vj : null,
      };
    },
  },
  methods: {
    monthLabel(monat) {
      return MONTHS[Number(monat) - 1] ?? String(monat);
    },
    fmtCell(v) {
      if (v === null || v === undefined) return '–';
      return EUR.format(v);
    },
    fmtDelta,
    deltaClass,
    emptyClass(v) {
      return v === null || v === undefined ? 'is-empty' : '';
    },
    deltaClassForMetric(v) {
      if (v === null || v === undefined) return 'is-empty';
      if (this.isKostenMetric) return v <= 0 ? 'positive' : 'negative';
      return v >= 0 ? 'positive' : 'negative';
    },
  },
};
</script>

<style scoped>
/* Monatstabelle: Zahlen rechtsbündig, Hierarchie über Schrift statt Flächen,
   Gruppen (IST · PLAN/vs. Plan · VJ/vs. VJ) nur durch feine Linien getrennt. */
.ts-table-wrap {
  overflow-x: auto;
}

.ts-table {
  min-width: 1180px;
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  table-layout: fixed;
  font-size: 14px;
  font-variant-numeric: tabular-nums;
}

.ts-table th,
.ts-table td {
  padding: 10px 12px;
  text-align: right;
  white-space: nowrap;
  color: var(--ds-text);
}

/* Kopf: klein, grau, Versalien */
.ts-table th {
  padding-top: 4px;
  font-size: 11px;
  font-weight: 500;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--ds-text-subtlest);
  border-bottom: 1px solid var(--ds-border);
}

/* Erste Spalte: Zeilenname links, beim Scrollen fest */
.ts-table__rowhead,
.ts-table__metric {
  position: sticky;
  left: 0;
  z-index: 1;
  width: 110px;
  text-align: left !important;
  background: var(--ds-surface);
}

.ts-table__metric {
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.04em;
  color: var(--ds-text-subtle);
}

/* IST: Hauptzeile, dunkel und kräftig, mit Akzentpunkt */
.ts-table__row--ist td {
  font-weight: 600;
  color: var(--calm-ink, var(--ds-text));
}

.ts-table__row--ist .ts-table__metric {
  color: var(--ds-brand);
}

.ts-table__row--ist .ts-table__metric::before {
  content: '';
  display: inline-block;
  width: 6px;
  height: 6px;
  margin: 0 8px 1px 0;
  border-radius: 50%;
  background: var(--ds-brand);
}

.ts-table__row--plan td,
.ts-table__row--vj td {
  color: var(--ds-text-subtle);
}

/* Abweichungen: kleiner, farbig, schließen ihre Gruppe mit einer Linie ab */
.ts-table__row--delta td {
  padding-top: 2px;
  font-size: 12px;
  font-weight: 500;
  color: var(--ds-text-subtle);
  border-bottom: 1px solid var(--ds-border);
}

.ts-table tbody tr:last-child td {
  border-bottom: none;
}

.ts-table__row--ist td {
  border-bottom: 1px solid var(--ds-border);
}

.ts-table__metric--delta {
  padding-left: 26px !important;
  font-weight: 400 !important;
  letter-spacing: 0 !important;
  color: var(--ds-text-subtlest) !important;
}

.ts-table td.positive { color: var(--ds-success-text); }
.ts-table td.negative { color: var(--ds-danger-text); }

/* Leere Monate zurücknehmen */
.ts-table td.is-empty {
  color: var(--ds-border-input);
  font-weight: 400;
}

/* Kumuliert: eigene leicht getönte Spalte */
.ts-table__cumhead,
.ts-table__cum {
  width: 130px;
  background: var(--ds-surface-sunken);
}

.ts-table__cum {
  font-weight: 600;
}

.ts-table__cumhead {
  border-top-right-radius: 8px;
}

.ts-table tbody tr:last-child .ts-table__cum {
  border-bottom-right-radius: 8px;
}
</style>
